import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button, Card, Col, Layout, Row, Typography } from "antd";
import {
  CheckCircleOutlined,
  FilePdfOutlined,
  SendOutlined,
  WhatsAppOutlined,
} from "@ant-design/icons";
import CustomHeader from "../Header";
import Footer from "../Footer";
import "./PurchaseFeature.css";
import {
  purchaseAdvantages,
  purchaseDashboardFeatures,
  purchaseFeaturedCards,
  purchaseFeatureText,
  purchaseTargetAudience,
  purchaseTemplates,
} from "./purchaseFeatureConstant";
import PurchaseCardScroller from "./purchaseCardScroller";
import { scrollToElementById } from "../../../Utility/scrollUtils";
import { Helmet } from "react-helmet-async";

const { Header, Content } = Layout;
const { Title, Paragraph } = Typography;

const PurchaseFeature: React.FC = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    scrollToElementById("purchase");
  }, [pathname]);

  return (
    <div id="purchase">
      <Helmet>
        <title>Purchase Management Software to Boost Revenue Growth | Ease Margin</title>
        <meta
          name="description"
          content="Streamline your purchase process with powerful purchase management software. Track orders, manage suppliers, and improve procurement with EaseMargin."
        />
        <link
          rel="canonical" href="https://easemargin.com/services/sales-management" />
      </Helmet>

      <CustomHeader />
      <Layout className="purchaseFeature-layout">
        <Header className="purchaseFeature-header">
          <div className="purchaseFeature-header-inner">
            <Title level={1} className="purchaseFeature-title-large">
              {purchaseFeatureText.HERO_TITLE}
            </Title>
            <Paragraph className="purchaseFeature-paragraph-header">
              {purchaseFeatureText.HERO_DESCRIPTION}
            </Paragraph>
            <Link to="/requestdemo">
              <Button type="primary" size="large" className="btn-orange">
                Request a Demo
              </Button>
            </Link>
          </div>
        </Header>
        <Content className="purchaseFeature-content">
          <Title level={2} className="purchaseFeature-title-2nd">
            {purchaseFeatureText.INTRO_TITLE}
          </Title>
          <Paragraph className="purchaseFeature-paragraph-main">
            {purchaseFeatureText.INTRO_DESCRIPTION}
          </Paragraph>
          <Title level={3} className="purchaseFeature-title-3rd">
            {purchaseFeatureText.SALES_FLOW_TITLE}
          </Title>
          <PurchaseCardScroller />
          <Title level={3} className="purchaseFeature-title-3rd-marginTop">
            {purchaseFeatureText.MULTI_FORMAT_TITLE}
          </Title>
          <Row gutter={[24, 24]} style={{ marginTop: 20 }}>
            <Col xs={24} md={12}>
              <Card bordered={false} className="card-default">
                <Title level={4} style={{ color: "#722ed1" }}>
                  {purchaseFeatureText.DOC_FORMAT}
                </Title>
                <Row gutter={[24, 24]}>
                  <Col span={12}>
                    <Paragraph style={{ marginLeft: 20 }}>
                      <FilePdfOutlined
                        style={{ marginRight: 8, color: "#fa541c" }}
                      />
                      PDF
                    </Paragraph>
                    <Paragraph style={{ marginLeft: 20 }}>
                      🖨️ Printable A4
                    </Paragraph>
                    <Paragraph style={{ marginLeft: 20 }}>
                      ✉️ Email-ready attachments
                    </Paragraph>
                  </Col>
                </Row>
              </Card>
            </Col>
            <Col xs={24} md={12}>
              <Card bordered={false} className="card-default">
                <Title level={4} style={{ color: "#722ed1" }}>
                  {purchaseFeatureText.SHARING_STORE}
                </Title>
                <Row gutter={[0, 24]}>
                  <Col span={12}>
                    <Paragraph style={{ marginLeft: 20 }}>
                      <FilePdfOutlined
                        style={{ marginRight: 8, color: "#f5222d" }}
                      />
                      Download
                    </Paragraph>
                    <Paragraph style={{ marginLeft: 20 }}>
                      🖨️ Print directly
                    </Paragraph>
                    <Paragraph style={{ marginLeft: 20 }}>
                      <SendOutlined
                        style={{ marginRight: 8, color: "#1890ff" }}
                      />
                      Send via Email
                    </Paragraph>
                  </Col>
                  <Col span={12}>
                    <Paragraph style={{ marginLeft: 20 }}>
                      <WhatsAppOutlined
                        style={{ marginRight: 8, color: "#25D366" }}
                      />
                      Send via WhatsApp
                    </Paragraph>
                    <Paragraph style={{ marginLeft: 20 }}>
                      🔗 Share Document Links
                    </Paragraph>
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>
          <Row gutter={[24, 24]} style={{ marginTop: 40 }}>
            {purchaseTemplates.map((feature, index) => (
              <Col xs={24} md={12} key={index}>
                <Card bordered={false} className="card-default">
                  <Title level={4} className="purchaseFeature-title-flex">
                    <span style={{ marginRight: 8 }}>{feature.icon}</span>
                    {feature.title}
                  </Title>
                  <Paragraph className="purchaseFeature-paragraph-desc">
                    {feature.description}
                  </Paragraph>
                </Card>
              </Col>
            ))}
          </Row>
          <Title level={3} className="purchaseFeature-title-3rd-marginTop">
            {purchaseFeatureText.DASHBOARD_TITLE}
          </Title>
          <Row gutter={[24, 24]} style={{ marginBottom: 40 }}>
            {purchaseDashboardFeatures.map((feature, index) => (
              <Col xs={24} sm={12} key={index}>
                <Card
                  bordered={false}
                  className="card-default"
                  style={{ backgroundColor: "#F6FFED" }}
                >
                  <Paragraph className="purchaseFeature-paragraph-dashboard">
                    <CheckCircleOutlined
                      style={{ color: "#1890ff", marginRight: 8 }}
                    />
                    {feature}
                  </Paragraph>
                </Card>
              </Col>
            ))}
          </Row>

          <Row gutter={[24, 24]} style={{ marginTop: 40 }}>
            {purchaseFeaturedCards.map((feature, index) => (
              <Col xs={24} md={12} key={index}>
                <Card bordered={false} className="card-default">
                  <Title level={4} className="purchaseFeature-title-flex">
                    <span style={{ marginRight: 8 }}>{feature.icon}</span>
                    {feature.title}
                  </Title>
                  <Paragraph className="purchaseFeature-paragraph-desc">
                    {feature.description}
                  </Paragraph>
                </Card>
              </Col>
            ))}
          </Row>

          <Title level={3} className="purchaseFeature-title-3rd-marginTop">
            {purchaseFeatureText.ADVANTAGE_TITLE}
          </Title>
          <Row gutter={[16, 16]} style={{ marginBottom: 40 }}>
            {purchaseAdvantages.map((advantage, idx) => (
              <Col xs={24} sm={12} key={idx}>
                <Card
                  bordered={false}
                  className="card-default card-light"
                  style={{ backgroundColor: "#F6FFED" }}
                >
                  <Paragraph className="purchaseFeature-paragraph-dashboard">
                    <CheckCircleOutlined
                      style={{ color: "#1890ff", marginRight: 8 }}
                    />
                    {advantage}
                  </Paragraph>
                </Card>
              </Col>
            ))}
          </Row>
          <Title level={3} className="purchaseFeature-title-3rd-marginTop">
            {purchaseFeatureText.PERFECT_FOR_TITLE}
          </Title>
          <Row gutter={[16, 16]}>
            {purchaseTargetAudience.map((target, idx) => (
              <Col xs={24} sm={12} key={idx}>
                <Card
                  bordered={false}
                  className="card-default card-light"
                  style={{ backgroundColor: "#FFF1F0" }}
                >
                  <Paragraph className="purchaseFeature-paragraph-targetAudience">
                    <CheckCircleOutlined
                      style={{ color: "#1890ff", marginRight: 8 }}
                    />
                    {target}
                  </Paragraph>
                </Card>
              </Col>
            ))}
          </Row>
          <Card
            bordered={false}
            className="card-default purchaseFeature-card-footer"
          >
            <Title level={2} className="purchaseFeature-title-footer">
              {purchaseFeatureText.CTA_TITLE}
            </Title>
            <Paragraph className="purchaseFeature-paragraph-footer">
              {purchaseFeatureText.CTA_DESCRIPTION}
            </Paragraph>
            <Link to="/requestdemo">
              <Button type="primary" size="large" className="btn-orange">
                {purchaseFeatureText.CTA_BUTTON}
              </Button>
            </Link>
          </Card>
        </Content>
      </Layout>
      <Footer />
    </div>
  );
};

export default PurchaseFeature;
